import type { MaybeRefOrGetter } from 'vue';
import type { ResolvedMonospaceServiceAccount } from '#layers/data-model/public';
import { useOverlay } from '@nuxt/ui/composables';
import { computed, toValue } from 'vue';
import { useI18n } from 'vue-i18n';
import DeleteServiceAccountDialog from '#layers/access/components/ServiceAccounts/DeleteServiceAccountDialog.vue';
import { usePaginatedListing } from '#layers/data-model/public';
import { useSystemEntityStore } from '#layers/runtime/public';
import { useToastResultHandling } from '#layers/ui/public';

const SERVICE_ACCOUNT_COLLECTION = 'MonospaceServiceAccount';

interface UseServiceAccountsOptions {
	/** Whether to execute the service-account listing query. */
	enabled?: MaybeRefOrGetter<boolean>;
}

/** Provides service-account listing pagination and account actions. */
export function useServiceAccounts(options: UseServiceAccountsOptions = {}) {
	const { t } = useI18n();
	const store = useSystemEntityStore();
	const overlay = useOverlay();
	const isEnabled = computed(() => options.enabled === undefined || toValue(options.enabled));

	const {
		items: serviceAccounts,
		pageNumber,
		pageSize,
		totalItemsCount,
		currentItemsCount,
		isLoading,
	} = usePaginatedListing<ResolvedMonospaceServiceAccount>({
		store,
		collectionApiName: SERVICE_ACCOUNT_COLLECTION,
		enabled: isEnabled,
		createQuery: (queryPageSize) => store.MonospaceServiceAccount.query((q) => q.many({
			enabled: isEnabled.value,
			fetchPolicy: 'cache-and-fetch',
			include: { workspaces: { include: { workspace: {} } } },
			pageIndex: 0,
			pageSize: queryPageSize.value,
			resultMode: 'responseRefs',
			sort: [
				{ fullName: { direction: 'asc' } },
				{ id: { direction: 'asc' } },
			],
		})),
	});

	const { onSuccess: deleteSuccess, onError: deleteError } = useToastResultHandling({
		success: {
			title: t('service_accounts.delete_success'),
		},
		error: {
			title: t('service_accounts.delete_error'),
		},
	});

	async function remove(serviceAccount: ResolvedMonospaceServiceAccount) {
		const modal = overlay.create(DeleteServiceAccountDialog, {
			props: { serviceAccount },
		});

		const confirmed = await modal.open();

		if (!confirmed) return;

		try {
			// Delete all api keys associated with the service account
			// else we will get a foreign key constraint error
			const apiKeys = await store.MonospaceApiKey.query((q) => q.many({
				filter: { userId: serviceAccount.id },
				fetchPolicy: 'no-cache',
			}));
			await Promise.all(apiKeys.data.value.map((key) => store.MonospaceApiKey.delete(key.id)));

			await store.MonospaceServiceAccount.delete(serviceAccount.id);
			deleteSuccess();
		}
		catch (error) {
			deleteError(error);
		}
	}

	return {
		serviceAccounts,
		pageNumber,
		pageSize,
		totalItemsCount,
		currentItemsCount,
		isLoading,
		remove,
	};
}
